import type { CancellationQueueRow } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Sample Cancellation — the Query panel's state and its cascade (prototype v4).
//
// The server filters ONLY by the Date Received window.  Every other box works
// client-side over that set, and each box lists only the values that fit what
// is picked in the OTHER boxes — which is why this is pure code over rows, with
// no round-trip per box.  Pure functions, so the cascade is unit-testable.
// ─────────────────────────────────────────────────────────────────────────────

export type RangeKey = '1M' | '3M' | '6M' | '1Y' | 'All' | 'Custom';

/** Date Range presets — same set as the shared DateRangeSelect, plus Custom. */
export const RANGES: { key: RangeKey; label: string; days: number | null }[] = [
  { key: '1M',     label: '1M',     days: 30 },
  { key: '3M',     label: '3M',     days: 90 },
  { key: '6M',     label: '6M',     days: 180 },
  { key: '1Y',     label: '1Y',     days: 365 },
  { key: 'All',    label: 'All',    days: null },
  { key: 'Custom', label: 'Custom', days: null },
];

export type FacetKey =
  | 'jobId' | 'client' | 'projectId' | 'projectName' | 'siteName'
  | 'test' | 'method' | 'prepBatch' | 'status';

export const NO_PREP_BATCH = '(no prep batch)';
const STATUS_ORDER = ['Pending Sample Prep', 'Pending Result Entry', 'Cancelled'];

/** The boxes, in the order they appear (Sample Name sits after Site Name). */
export const FACETS: { key: FacetKey; label: string }[] = [
  { key: 'jobId',       label: 'Job ID' },
  { key: 'client',      label: 'Client' },
  { key: 'projectId',   label: 'Project ID' },
  { key: 'projectName', label: 'Project Name' },
  { key: 'siteName',    label: 'Site Name' },
  { key: 'test',        label: 'Test' },
  { key: 'method',      label: 'Method' },
  { key: 'prepBatch',   label: 'Prep Batch' },
  { key: 'status',      label: 'Status' },
];

export interface QueueQuery {
  range: RangeKey;
  /** ISO yyyy-MM-dd; '' = open-ended. */
  from: string;
  to: string;
  sampleName: string;
  picks: Record<FacetKey, string[]>;
}

const pad = (n: number) => String(n).padStart(2, '0');
/** Local calendar date as ISO yyyy-MM-dd (built from date parts — never toISOString,
 *  which would shift the day by the UTC offset). */
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** From / To for a preset; All (and Custom, until typed) is open-ended. */
export function rangeDates(key: RangeKey, today = new Date()): { from: string; to: string } {
  const days = RANGES.find((r) => r.key === key)?.days;
  if (days == null) return { from: '', to: '' };
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - days);
  return { from: iso(start), to: iso(today) };
}

export function emptyQuery(today = new Date()): QueueQuery {
  return {
    range: '1M', ...rangeDates('1M', today), sampleName: '',
    picks: { jobId: [], client: [], projectId: [], projectName: [], siteName: [],
             test: [], method: [], prepBatch: [], status: [] },
  };
}

/** A row's values for a box. Prep Batch has an explicit "none" bucket so tests
 *  not yet in a batch can be picked, and a test in SEVERAL batches (the server
 *  sends them as "PB1, PB2") counts under each one — as one combined value it
 *  could be found under neither. Every other blank is simply not offered. */
export function facetValues(r: CancellationQueueRow, k: FacetKey): string[] {
  const v = r[k] ?? '';
  if (k !== 'prepBatch') return v ? [v] : [];
  const batches = v.split(',').map((b) => b.trim()).filter(Boolean);
  return batches.length ? batches : [NO_PREP_BATCH];
}

/** Does the row fit the query's boxes (dates are the server's job)?  `skip`
 *  leaves one box out — that is how a box's own option list is computed. */
export function matches(r: CancellationQueueRow, q: QueueQuery, skip?: FacetKey): boolean {
  const name = q.sampleName.trim().toLowerCase();
  if (name && !(r.sampleName ?? '').toLowerCase().includes(name)) return false;
  return FACETS.every(({ key }) =>
    key === skip || q.picks[key].length === 0
      || facetValues(r, key).some((v) => q.picks[key].includes(v)));
}

/** Options for one box = the values present in rows that fit every OTHER box,
 *  plus anything already ticked (so it stays visible and can be unticked). */
export function facetOptions(pool: CancellationQueueRow[], q: QueueQuery, k: FacetKey): string[] {
  const set = new Set<string>(q.picks[k]);
  for (const r of pool) {
    if (!matches(r, q, k)) continue;
    for (const v of facetValues(r, k)) set.add(v);
  }
  const list = [...set];
  if (k === 'status') return list.sort((a, b) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b));
  if (k === 'jobId')  return list.sort((a, b) => b.localeCompare(a));          // newest job first
  return list.sort((a, b) => a.localeCompare(b));
}

/** Do two queries ask for the same thing?  Used to tell the user that the boxes
 *  were changed since the last Search, so the grid is still showing the old one. */
export function sameQuery(a: QueueQuery, b: QueueQuery): boolean {
  return a.from === b.from && a.to === b.to && a.sampleName.trim() === b.sampleName.trim()
    && FACETS.every(({ key }) => {
      const x = a.picks[key], y = b.picks[key];
      return x.length === y.length && x.every((v) => y.includes(v));
    });
}

/** One-line summary of the applied query for the collapsed panel header.
 *  `fmtDate` is the shared date formatter (dates are never formatted inline). */
export function querySummary(q: QueueQuery, fmtDate: (iso: string) => string): string {
  const parts: string[] = [];
  if (q.range === 'All') parts.push('Received: any date');
  else {
    const span = `${q.from ? fmtDate(q.from) : '…'} – ${q.to ? fmtDate(q.to) : '…'}`;
    parts.push(q.range === 'Custom' ? `Received: ${span}` : `Received: ${q.range} (${span})`);
  }
  if (q.sampleName.trim()) parts.push(`Sample Name contains "${q.sampleName.trim()}"`);
  for (const { key, label } of FACETS) {
    const p = q.picks[key];
    if (p.length) parts.push(`${label}: ${p.length <= 2 ? p.join(', ') : `${p.length} selected`}`);
  }
  return parts.join(' · ');
}
